import { Component, Input, OnInit, signal, computed } from '@angular/core';
import { BaseModalComponent } from '@card-software/library';
import {
  ProductionDashboardWorkOrderDto,
  UomAndQuantityKeyDto,
  WorkOrderType,
  WorkOrderTerm,
  WorkOrderStatus,
} from '@app/models/production/production-dashboard';

export interface QuantityRow {
  label: string;
  quantity: string;
  rollsCount: number;
  percentage?: number;
}

@Component({
  selector: 'app-work-order-quantities-modal',
  templateUrl: './work-order-quantities-modal.component.html',
  styleUrl: './work-order-quantities-modal.component.scss',
})
export class WorkOrderQuantitiesModalComponent extends BaseModalComponent<null> implements OnInit {
  @Input() workOrder!: ProductionDashboardWorkOrderDto;

  requiredQuantity = signal<number>(0);

  quantityRows = computed<QuantityRow[]>(() => {
    const wo = this.workOrder;
    if (!wo) return [];

    const required = this.requiredQuantity();
    const rollQty = wo.totalCurrentRollQuantities;

    const rows: QuantityRow[] = [
      {
        label: 'Required Quantity',
        quantity: this.formatQuantity(wo.requiredQuantity),
        rollsCount: 0,
        percentage: 100,
      },
      {
        label: 'Knitted',
        quantity: this.formatQuantity(rollQty.knittedQuantity),
        rollsCount: rollQty.knittedRollsCount,
        percentage: this.calculatePercentage(rollQty.knittedQuantity, required),
      },
      {
        label: 'Batched',
        quantity: this.formatQuantity(rollQty.batchedQuantity),
        rollsCount: rollQty.batchedRollsCount,
        percentage: this.calculatePercentage(rollQty.batchedQuantity, required),
      },
      {
        label: 'Dyed',
        quantity: this.formatQuantity(rollQty.dyedQuantity),
        rollsCount: rollQty.dyedRollsCount,
        percentage: this.calculatePercentage(rollQty.dyedQuantity, required),
      },
      {
        label: 'Weighed',
        quantity: this.formatQuantity(rollQty.weighedQuantity),
        rollsCount: rollQty.weighedRollsCount,
        percentage: this.calculatePercentage(rollQty.weighedQuantity, required),
      },
      {
        label: 'Finished Goods',
        quantity: this.formatQuantity(rollQty.finishedGoodsQuantity),
        rollsCount: rollQty.finishedGoodsRollsCount,
        percentage: this.calculatePercentage(rollQty.finishedGoodsQuantity, required),
      },
      {
        label: 'Pending Dispatch',
        quantity: this.formatQuantity(rollQty.pendingDispatchInstructionQuantity),
        rollsCount: rollQty.pendingDispatchInstructionRollsCount,
        percentage: this.calculatePercentage(rollQty.pendingDispatchInstructionQuantity, required),
      },
      {
        label: 'Dispatched',
        quantity: this.formatQuantity(rollQty.dispatchedQuantity),
        rollsCount: rollQty.dispatchedRollsCount,
        percentage: this.calculatePercentage(rollQty.dispatchedQuantity, required),
      },
    ];

    return rows;
  });

  missingQuantity = computed<string>(() => {
    const required = this.requiredQuantity();
    const dispatched = this.getQuantityValue(this.workOrder?.totalCurrentRollQuantities?.dispatchedQuantity);
    const missing = required - dispatched;
    return missing > 0 ? `${missing.toLocaleString(undefined, { maximumFractionDigits: 2 })} lbs` : '0 lbs';
  });

  ngOnInit(): void {
    if (this.workOrder?.requiredQuantity?.length > 0) {
      this.requiredQuantity.set(this.workOrder.requiredQuantity[0].quantity);
    }
  }

  onClose(): void {
    this.close({ action: 'close', data: null });
  }

  getWorkOrderTypeLabel(type: WorkOrderType): string {
    return WorkOrderType[type] || `Type ${type}`;
  }

  getWorkOrderTermLabel(term: WorkOrderTerm): string {
    const termLabels: Record<number, string> = {
      0: 'Commission Knitting',
      1: 'Full Knitting',
      2: 'Dyeing',
      3: 'Full Package',
      4: 'Commission Knitting & Dyeing',
    };
    return termLabels[term] || `Term ${term}`;
  }

  getWorkOrderStatusLabel(status: WorkOrderStatus): string {
    return WorkOrderStatus[status] || `Status ${status}`;
  }

  private formatQuantity(quantities: UomAndQuantityKeyDto[] | undefined): string {
    if (!quantities || quantities.length === 0) return '0 lbs';
    const qty = quantities[0];
    return `${qty.quantity.toLocaleString(undefined, { maximumFractionDigits: 2 })} lbs`;
  }

  private getQuantityValue(quantities: UomAndQuantityKeyDto[] | undefined): number {
    if (!quantities || quantities.length === 0) return 0;
    return quantities[0].quantity;
  }

  private calculatePercentage(quantities: UomAndQuantityKeyDto[] | undefined, required: number): number {
    if (!required || required === 0) return 0;
    const value = this.getQuantityValue(quantities);
    return Math.round((value / required) * 100);
  }
}
